import React, { useState, useEffect } from 'react';
import { AlertCircle, ArrowLeft, ChevronRight, Search } from 'lucide-react';
import ProtocolTemplate from '../protocols/ProtocolTemplate';
import { protocolsData } from '../protocols/protocolsData';
import { useScrollToTop } from '../../hooks/useScrollReset';

const EmergencyProtocolsScreen = ({ onBack, initialProtocol }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('acls');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  // Opening a protocol (and returning to the list) starts at the top
  useScrollToTop([selectedProtocol]);

  // Auto-select protocol if provided from search
  useEffect(() => {
    if (initialProtocol) {
      setSelectedProtocol(initialProtocol);
    }
  }, [initialProtocol]);

  // Filter protocols based on search query
  const filterProtocols = (protocols) => {
    if (!searchQuery.trim()) return protocols;

    const query = searchQuery.toLowerCase();
    return protocols.filter(protocol =>
      protocol.title.toLowerCase().includes(query) ||
      protocol.category.toLowerCase().includes(query) ||
      (protocol.searchTerms && protocol.searchTerms.some(term => term.toLowerCase().includes(query)))
    );
  };

  // Organize protocols into 4 main tabs
  const tabs = [
    {
      id: 'acls',
      name: 'ACLS',
      categories: [
        {
          name: 'ACLS Algorithms',
          protocols: protocolsData.filter(p => p.categoryId === 'acls')
        }
      ]
    },
    {
      id: 'periop',
      name: 'Periop',
      categories: [
        {
          name: 'General Emergency',
          protocols: protocolsData.filter(p => p.categoryId === 'general')
        },
        {
          name: 'Physiologic Crises',
          protocols: protocolsData.filter(p => p.categoryId === 'physiologic')
        },
        {
          name: 'Airway Emergencies',
          protocols: protocolsData.filter(p => p.categoryId === 'airway')
        }
      ]
    },
    {
      id: 'cardiac',
      name: 'Cardiac',
      categories: [
        {
          name: 'Cardiac Anesthesia',
          protocols: protocolsData.filter(p => p.categoryId === 'cardiac')
        },
        {
          name: 'Thoracic Surgery',
          protocols: protocolsData.filter(p => p.categoryId === 'thoracic')
        },
        {
          name: 'Perfusion / CPB',
          protocols: protocolsData.filter(p => p.categoryId === 'perfusion')
        }
      ]
    },
    {
      id: 'specialty',
      name: 'Specialty',
      categories: [
        {
          name: 'Obstetric Emergencies',
          protocols: protocolsData.filter(p => p.categoryId === 'obstetric')
        },
        {
          name: 'Neuroanesthesia',
          protocols: protocolsData.filter(p => p.categoryId === 'neuro')
        }
      ]
    }
  ];

  const hasSearchResults = searchQuery.trim() !== '';

  // Get current tab's content
  const currentTab = tabs.find(tab => tab.id === activeTab);

  // Filter protocols - search across ALL tabs if searching, otherwise show current tab
  const displayCategories = hasSearchResults
    ? // Search mode: search across ALL tabs
      tabs.flatMap(tab =>
        tab.categories.map(cat => ({
          ...cat,
          tabName: tab.name, // Include which tab this is from
          protocols: filterProtocols(cat.protocols)
        }))
      ).filter(cat => cat.protocols.length > 0)
    : // Browse mode: show current tab only
      currentTab.categories;

  // Handle protocol navigation from cross-references
  const handleProtocolNavigation = (protocolId) => {
    // Find protocol by matching against title (converted to ID format)
    const targetProtocol = protocolsData.find(p => {
      const titleAsId = p.title.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '');
      const searchId = protocolId.toLowerCase().replace(/[()]/g, '');
      return titleAsId === searchId || titleAsId.includes(searchId) || searchId.includes(titleAsId);
    });

    if (targetProtocol) {
      setSelectedProtocol(targetProtocol);
    }
  };

  // If a protocol is selected, show it full-screen
  if (selectedProtocol) {
    return (
      <ProtocolTemplate
        protocol={selectedProtocol}
        onClose={() => setSelectedProtocol(null)}
        onNavigateToProtocol={handleProtocolNavigation}
        breadcrumb={selectedProtocol.category}
      />
    );
  }

  // Otherwise, show the protocol list
  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 pb-8">
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} /><span>Back</span>
        </button>
        <div className="flex items-center gap-4">
          <AlertCircle size={40} />
          <div>
            <h1 className="text-2xl font-bold">Emergency Protocols</h1>
            <p className="text-red-100 text-sm mt-1">Crisis management & memory aids</p>
          </div>
        </div>
      </div>

      {/* Search Bar - Sticky */}
      <div className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 px-6 pt-6 pb-3">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 focus-within:border-red-500 dark:focus-within:border-red-400 transition-all">
          <div className="relative">
            <Search className="absolute left-4 top-4 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search protocols, drugs, symptoms..."
              className="w-full pl-12 pr-4 py-4 rounded-xl focus:outline-none text-gray-800 dark:text-white bg-transparent placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>
        {hasSearchResults && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Showing {displayCategories.reduce((sum, cat) => sum + cat.protocols.length, 0)} protocol(s)
          </p>
        )}
      </div>

      {/* Segmented Control for Tabs */}
      <div className="px-6 mb-4">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-1 grid grid-cols-4 gap-1" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => { setActiveTab(tab.id); }}
              className={`py-2 px-2 rounded-lg font-semibold text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-red-700 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Protocol Lists */}
      <div className="px-6 pb-6 space-y-4">
        {displayCategories.map((category, catIdx) => (
          <div key={catIdx}>
            {/* Category Header */}
            {(displayCategories.length > 1 || hasSearchResults) && (
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                {hasSearchResults && category.tabName && `${category.tabName} • `}{category.name}
              </h3>
            )}

            {/* Protocols in this category */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-md">
              {category.protocols.map((protocol, idx) => (
                <button
                  key={idx}
                  onClick={() => { setSelectedProtocol(protocol); }}
                  className={`w-full p-4 text-left hover:bg-red-50 dark:hover:bg-red-900/10 transition-all flex items-center justify-between ${
                    idx !== category.protocols.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                  } ${!protocol.content ? 'opacity-60' : ''}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">{protocol.title}</div>
                    {!protocol.content && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 italic">Coming soon</span>
                    )}
                  </div>
                  <ChevronRight size={18} className="text-red-600 dark:text-red-500" />
                </button>
              ))}
            </div>
          </div>
        ))}

        {displayCategories.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No protocols found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyProtocolsScreen;
